import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Collapse,
  Button,
  CssBaseline,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import RouterIcon from '@mui/icons-material/Router';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import VideocamIcon from '@mui/icons-material/Videocam';
import SensorsIcon from '@mui/icons-material/Sensors';
import HubIcon from '@mui/icons-material/Hub';
import DevicesIcon from '@mui/icons-material/Devices';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SpeedIcon from '@mui/icons-material/Speed';
import CodeIcon from '@mui/icons-material/Code';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { api } from '../services/api';

import { MAP_UI } from '../theme/mapViewUi';

const JUGANU_YELLOW = MAP_UI.accentSoft;
const JUGANU_GREEN = MAP_UI.deviceOnlineBorder;
const JUGANU_RED = MAP_UI.deviceOfflineBorder;

const dashboardTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: MAP_UI.accent, light: MAP_UI.accentSoft, dark: MAP_UI.accentDark },
    background: {
      default: '#070B12',
      paper: '#0B1220',
    },
    divider: '#1E2A3B',
    text: {
      primary: '#E7EDF7',
      secondary: '#9AA7BD',
    },
    action: {
      hover: 'rgba(255,255,255,0.06)',
      disabled: 'rgba(255,255,255,0.18)',
    },
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: '"Inter","Roboto","Helvetica","Arial",sans-serif',
  },
});

const JuganuMark = ({ size = 28 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: 1.25,
      bgcolor: '#0A0F1A',
      border: '1px solid #1E2A3B',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 6px 18px rgba(0,0,0,0.45)',
    }}
  >
    <svg width={Math.round(size * 0.65)} height={Math.round(size * 0.65)} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="2.2" fill={JUGANU_YELLOW} />
      <circle cx="5" cy="7" r="1.9" fill={JUGANU_YELLOW} opacity="0.9" />
      <circle cx="19" cy="7" r="1.9" fill={JUGANU_YELLOW} opacity="0.9" />
      <circle cx="5" cy="17" r="1.9" fill={JUGANU_YELLOW} opacity="0.9" />
      <circle cx="19" cy="17" r="1.9" fill={JUGANU_YELLOW} opacity="0.9" />
      <path d="M7 8.4L10.6 11.1M17 8.4L13.4 11.1M7 15.6L10.6 12.9M17 15.6L13.4 12.9" stroke={JUGANU_YELLOW} strokeWidth="1.2" opacity="0.55" />
    </svg>
  </Box>
);

const formatUtcTime = (date) => {
  if (!date) return '--:--:-- UTC';
  return (
    date.toLocaleTimeString('en-US', { hour12: false, timeZone: 'UTC' }) + ' UTC'
  );
};

const getDeviceIcon = (deviceType) => {
  const type = (deviceType || '').toLowerCase();
  if (type.includes('gateway') || type.includes('cap')) return <RouterIcon />;
  if (type.includes('mesh') || type.includes('node')) return <WifiTetheringIcon />;
  if (type.includes('camera') || type.includes('ai') || type.includes('nvidia')) return <VideocamIcon />;
  if (type.includes('sensor') || type.includes('hub')) return <SensorsIcon />;
  return <HubIcon />;
};

const getLatencyColor = (latency) => {
  if (latency < 50) return JUGANU_GREEN;
  if (latency < 100) return JUGANU_YELLOW;
  return JUGANU_RED;
};

const getPacketLossColor = (loss) => {
  if (loss < 1) return JUGANU_GREEN;
  if (loss < 5) return JUGANU_YELLOW;
  return JUGANU_RED;
};

const getHealthIndicator = (connected, offline) => {
  const total = connected + offline;
  if (total === 0) return { color: '#9AA7BD', label: 'N/A', status: 'unknown' };
  const ratio = (connected / total) * 100;
  if (ratio >= 100) return { color: JUGANU_GREEN, label: 'Healthy', status: 'healthy' };
  if (ratio >= 80) return { color: JUGANU_YELLOW, label: 'Warning', status: 'warning' };
  return { color: JUGANU_RED, label: 'Critical', status: 'critical' };
};

const getConnectivityRatio = (connected, offline) => {
  const total = connected + offline;
  if (total === 0) return null;
  return (connected / total) * 100;
};

const computeSystemStatus = (sourceData) => {
  let worstStatus = 'healthy';
  let worstType = null;
  let worstRatio = 100;

  Object.entries(sourceData || {}).forEach(([type, data]) => {
    let connected = 0;
    let offline = 0;

    if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
      connected = typeof data.connected === 'number' ? data.connected : 0;
      offline = typeof data.offline === 'number' ? data.offline : 0;
    } else if (typeof data === 'number') {
      connected = data;
      offline = 0;
    }

    const total = connected + offline;
    if (total === 0) return;
    const ratio = (connected / total) * 100;

    if (ratio < worstRatio) {
      worstRatio = ratio;
      worstType = type;
    }

    if (ratio < 80) {
      worstStatus = 'critical';
    } else if (ratio < 100 && worstStatus !== 'critical') {
      worstStatus = 'degraded';
    }
  });

  if (worstStatus === 'critical') {
    return {
      status: 'critical',
      label: 'System Critical',
      bgColor: JUGANU_RED,
      textColor: '#FFFFFF',
      worstType,
    };
  }
  if (worstStatus === 'degraded') {
    return {
      status: 'degraded',
      label: 'System Degraded',
      bgColor: JUGANU_YELLOW,
      textColor: '#000000',
      worstType,
    };
  }
  return { status: 'healthy', label: 'System Healthy', bgColor: JUGANU_GREEN, textColor: '#000000', worstType: null };
};

const NetworkStatusPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [networkData, setNetworkData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [expandedVersions, setExpandedVersions] = useState({});

  const fetchNetworkStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getProjectNetworkStatus();
      setNetworkData(data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching network status:', err);
      setError(err.message || 'Failed to fetch network status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNetworkStatus();
  }, [fetchNetworkStatus]);

  const toggleVersionExpand = (deviceType) => {
    setExpandedVersions((prev) => ({ ...prev, [deviceType]: !prev[deviceType] }));
  };

  const projectIdRaw = networkData?.project_id || 'N/A';
  const projectId = useMemo(() => {
    if (!projectIdRaw || projectIdRaw === 'N/A') return projectIdRaw;
    const asString = String(projectIdRaw);
    // Screenshot shows "Project P-1024" formatting.
    return asString.startsWith('P-') ? asString : `P-${asString}`;
  }, [projectIdRaw]);

  // Memoize to keep stable deps for useMemo hooks.
  const devicesByType = useMemo(() => networkData?.devices_by_type || {}, [networkData]);
  const devicesByTypeDetailed = useMemo(() => networkData?.devices_by_type_detailed || {}, [networkData]);
  const sourceData = useMemo(() => {
    return Object.keys(devicesByTypeDetailed).length > 0 ? devicesByTypeDetailed : devicesByType;
  }, [devicesByType, devicesByTypeDetailed]);

  // Topology metrics (values must show '-' if not provided)
  const maxNodesPerCap =
    networkData?.topology_metrics?.max_nodes_per_cap ??
    networkData?.topology?.max_nodes_per_cap ??
    null;
  const minNodesPerCap =
    networkData?.topology_metrics?.min_nodes_per_cap ??
    networkData?.topology?.min_nodes_per_cap ??
    null;
  const maxTreeDepth =
    networkData?.topology_metrics?.max_tree_depth ??
    networkData?.topology?.max_tree_depth ??
    networkData?.topology?.max_hops ??
    null;

  // Network quality metrics. The headline "Avg Latency" widget shows the
  // average across connected devices of each device's median round-trip
  // latency to its parent (ms). Older field names are kept as fallbacks
  // for forward-compat with future API tweaks.
  const networkQuality = networkData?.network_quality_metrics || networkData?.network_quality || {};
  const avgLatency =
    networkQuality.avg_parent_latency_ms ??
    networkQuality.avg_latency ??
    networkQuality.average_latency_ms ??
    null;
  const packetLoss =
    networkQuality.avg_packet_loss_pct ??
    networkQuality.avg_packet_loss ??
    networkQuality.packet_loss_percent ??
    null;

  const softwareVersionsByType =
    networkData?.software_versions_by_type || networkData?.software_versions || {};

  // Per-device-type summary cards (UI-only). Do NOT show global totals or aggregate across types.
  const deviceTypeSummaries = useMemo(() => {
    const statusRank = (status) => {
      if (status === 'critical') return 0;
      if (status === 'warning') return 1;
      if (status === 'healthy') return 2;
      return 3;
    };

    return Object.entries(devicesByType || {})
      .map(([type, data]) => {
        let connected = 0;
        let offline = 0;

        if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
          connected = typeof data.connected === 'number' ? data.connected : 0;
          offline = typeof data.offline === 'number' ? data.offline : 0;
        } else if (typeof data === 'number') {
          connected = data;
          offline = 0;
        }

        const total = connected + offline;
        const health = getHealthIndicator(connected, offline);

        return { type, connected, offline, total, health };
      })
      .sort((a, b) => {
        const byStatus = statusRank(a.health.status) - statusRank(b.health.status);
        if (byStatus !== 0) return byStatus;
        if (b.offline !== a.offline) return b.offline - a.offline;
        return a.type.localeCompare(b.type);
      });
  }, [devicesByType]);

  const systemStatus = useMemo(() => computeSystemStatus(sourceData), [sourceData]);

  if (loading && !networkData) {
    return (
      <ThemeProvider theme={dashboardTheme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            bgcolor: 'background.default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress sx={{ color: JUGANU_YELLOW }} />
        </Box>
      </ThemeProvider>
    );
  }

  if (error && !networkData) {
    return (
      <ThemeProvider theme={dashboardTheme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            onClick={fetchNetworkStatus}
            startIcon={<RefreshIcon />}
            sx={{
              bgcolor: JUGANU_YELLOW,
              color: '#000',
              fontWeight: 700,
              '&:hover': { bgcolor: JUGANU_YELLOW },
            }}
          >
            Retry
          </Button>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={dashboardTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 2, sm: 3 } }}>
        {/* Top Header Bar (matches screenshot) */}
        <Paper
          sx={{
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            bgcolor: '#0A0F1A',
            px: 2.5,
            py: 2,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 10px 28px rgba(0,0,0,0.55)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <JuganuMark size={34} />
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.2 }}>
                Project {projectId}
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.82rem', mt: 0.25 }}>
                Network &amp; Installation Status
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography
                sx={{
                  fontSize: '0.62rem',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: 1.6,
                  color: 'text.secondary',
                }}
              >
                Last Updated
              </Typography>
              <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: 'text.primary' }}>
                {formatUtcTime(lastUpdated)}
              </Typography>
            </Box>
            <IconButton
              onClick={fetchNetworkStatus}
              sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                border: '1px solid #1E2A3B',
                bgcolor: 'rgba(255,255,255,0.03)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
              }}
            >
              <RefreshIcon sx={{ fontSize: 18, color: '#9AA7BD' }} />
            </IconButton>
          </Box>
        </Paper>

        {/* System Status Banner */}
        {systemStatus.status !== 'healthy' && (
          <Box sx={{ mb: 3 }}>
            <Paper
              sx={{
                bgcolor: systemStatus.bgColor,
                color: systemStatus.textColor,
                borderRadius: 2,
                overflow: 'hidden',
                position: 'relative',
                boxShadow: '0 12px 30px rgba(0,0,0,0.55)',
                border: `1px solid ${systemStatus.bgColor}80`,
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  backgroundImage:
                    `url("data:image/svg+xml,%3Csvg width='24' height='24' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='1' cy='1' r='1' fill='rgba(0,0,0,0.06)'/%3E%3C/svg%3E")`,
                  opacity: 0.35,
                }}
              />
              <Box
                sx={{
                  position: 'relative',
                  px: 3,
                  py: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      bgcolor:
                        systemStatus.status === 'degraded'
                          ? 'rgba(0,0,0,0.10)'
                          : 'rgba(255,255,255,0.10)',
                      p: 1.4,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {systemStatus.status === 'critical' ? (
                      <ErrorIcon sx={{ fontSize: 30, fontWeight: 900 }} />
                    ) : (
                      <WarningIcon sx={{ fontSize: 30, fontWeight: 900 }} />
                    )}
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 900,
                        textTransform: 'uppercase',
                        letterSpacing: 2.2,
                        mb: 0.4,
                        fontSize: '0.98rem',
                      }}
                    >
                      {systemStatus.label}
                    </Typography>
                    <Typography sx={{ fontWeight: 600, opacity: 0.82, fontSize: '0.86rem' }}>
                      Connectivity issues detected in {systemStatus.worstType || 'device'} subnet.
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    px: 2,
                    py: 1.2,
                    borderRadius: 1.25,
                    bgcolor:
                      systemStatus.status === 'degraded'
                        ? 'rgba(255,255,255,0.20)'
                        : 'rgba(255,255,255,0.12)',
                    border: '1px solid rgba(255,255,255,0.14)',
                    minWidth: 92,
                    textAlign: 'left',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.62rem',
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: 1.6,
                      opacity: 0.8,
                      mb: 0.35,
                    }}
                  >
                    Impact
                  </Typography>
                  <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1.0rem' }}>
                    {systemStatus.status === 'critical' ? 'High' : 'Low'}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        )}

        {/* Per-Device-Type Summary Cards (no global totals) */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {deviceTypeSummaries.map(({ type, connected, offline, health }) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={type}>
              <Paper
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  border: `1px solid ${health.color}33`,
                  boxShadow: '0 10px 24px rgba(0,0,0,0.45)',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 6,
                    bgcolor: health.color,
                    opacity: 0.9,
                  }}
                />

                <Box sx={{ p: 2, pl: 2.25 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, mb: 1.4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                      <Box
                        sx={{
                          p: 0.85,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          color: 'text.secondary',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {getDeviceIcon(type)}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', lineHeight: 1.2 }} noWrap>
                          {type}
                        </Typography>
                        <Typography
                          sx={{
                            mt: 0.35,
                            fontSize: '0.62rem',
                            fontWeight: 800,
                            textTransform: 'uppercase',
                            letterSpacing: 1.6,
                            color: 'text.secondary',
                          }}
                        >
                          Device Type
                        </Typography>
                      </Box>
                    </Box>

                    <Chip
                      label={health.label}
                      size="small"
                      sx={{
                        bgcolor: `${health.color}1A`,
                        color: health.color,
                        border: `1px solid ${health.color}33`,
                        fontWeight: 900,
                        fontSize: '0.62rem',
                        height: 24,
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        '& .MuiChip-label': { px: 1.25 },
                      }}
                      icon={
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: health.color,
                          }}
                        />
                      }
                    />
                  </Box>

                  <Grid container spacing={1.25}>
                    <Grid item xs={6}>
                      <Box
                        sx={{
                          p: 1.25,
                          bgcolor: 'action.hover',
                          borderRadius: 1.25,
                          border: 1,
                          borderColor: 'divider',
                        }}
                      >
                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 1.2 }}>
                          Connected
                        </Typography>
                        <Typography sx={{ mt: 0.4, fontFamily: 'monospace', fontWeight: 900, fontSize: '1.25rem', color: 'text.primary' }}>
                          {connected.toLocaleString()}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6}>
                      <Box
                        sx={{
                          p: 1.25,
                          borderRadius: 1.25,
                          border: `1px solid ${offline > 0 ? `${JUGANU_RED}66` : 'divider'}`,
                          bgcolor: offline > 0 ? `${JUGANU_RED}1F` : 'action.hover',
                          boxShadow: offline > 0 ? `0 0 0 1px ${JUGANU_RED}22 inset` : 'none',
                        }}
                      >
                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 900, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 1.2 }}>
                          Offline
                        </Typography>
                        <Typography
                          sx={{
                            mt: 0.4,
                            fontFamily: 'monospace',
                            fontWeight: 900,
                            fontSize: '1.25rem',
                            color: offline > 0 ? JUGANU_RED : 'text.primary',
                          }}
                        >
                          {offline.toLocaleString()}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>
          ))}

          {deviceTypeSummaries.length === 0 && (
            <Grid item xs={12}>
              <Paper sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                  No device type data available
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>

        <Grid container spacing={3}>
          {/* Devices by Type (Primary Section) */}
          <Grid item xs={12} xl={8}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: '0.95rem',
                }}
              >
                <Box sx={{ bgcolor: `${JUGANU_YELLOW}33`, color: JUGANU_YELLOW, p: 0.6, borderRadius: 1 }}>
                  <DevicesIcon sx={{ fontSize: 18 }} />
                </Box>
                Devices by Type
              </Typography>
              <Button
                size="small"
                sx={{ textTransform: 'uppercase', fontSize: '0.62rem', fontWeight: 800, letterSpacing: 1.2, color: 'text.secondary' }}
              >
                View All
              </Button>
            </Box>

            <Paper
              sx={{
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                overflow: 'hidden',
                bgcolor: 'background.paper',
                boxShadow: '0 12px 30px rgba(0,0,0,0.55)',
              }}
            >
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell
                        sx={{
                          py: 2,
                          fontSize: '0.62rem',
                          fontWeight: 800,
                          textTransform: 'uppercase',
                          letterSpacing: 2,
                          color: 'text.secondary',
                          borderColor: 'divider',
                        }}
                      >
                        Device Type
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 2,
                          fontSize: '0.62rem',
                          fontWeight: 800,
                          textTransform: 'uppercase',
                          letterSpacing: 2,
                          color: 'text.secondary',
                          width: '36%',
                          borderColor: 'divider',
                        }}
                      >
                        Connectivity
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          py: 2,
                          fontSize: '0.62rem',
                          fontWeight: 800,
                          textTransform: 'uppercase',
                          letterSpacing: 2,
                          color: 'text.secondary',
                          borderColor: 'divider',
                        }}
                      >
                        Count
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          py: 2,
                          fontSize: '0.62rem',
                          fontWeight: 800,
                          textTransform: 'uppercase',
                          letterSpacing: 2,
                          color: 'text.secondary',
                          borderColor: 'divider',
                        }}
                      >
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(sourceData || {})
                      .map(([type, data]) => {
                        let connected = 0;
                        let offline = 0;

                        if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
                          connected = typeof data.connected === 'number' ? data.connected : 0;
                          offline = typeof data.offline === 'number' ? data.offline : 0;
                        } else if (typeof data === 'number') {
                          connected = data;
                          offline = 0;
                        }

                        const total = connected + offline;
                        if (total === 0) return null;

                        const ratio = getConnectivityRatio(connected, offline);
                        const health = getHealthIndicator(connected, offline);
                        const connectedPercent = ratio !== null ? ratio : 0;
                        const offlinePercent = ratio !== null ? 100 - ratio : 0;

                        return (
                          <TableRow
                            key={type}
                            sx={{
                              '&:hover': { bgcolor: 'action.hover' },
                              transition: 'background-color 0.2s',
                            }}
                          >
                            <TableCell sx={{ borderColor: 'divider' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box
                                  sx={{
                                    p: 0.85,
                                    borderRadius: 1,
                                    bgcolor: 'action.hover',
                                    color: 'text.secondary',
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  {getDeviceIcon(type)}
                                </Box>
                                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{type}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ borderColor: 'divider' }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.85 }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    fontSize: '0.65rem',
                                    fontFamily: 'monospace',
                                    color: 'text.secondary',
                                  }}
                                >
                                  <span>{connected} Connected</span>
                                  {offline > 0 ? (
                                    <span style={{ color: JUGANU_RED, fontWeight: 800 }}>
                                      {offline} Offline
                                    </span>
                                  ) : (
                                    <span style={{ opacity: 0.6 }}>0 Offline</span>
                                  )}
                                </Box>
                                <Box
                                  sx={{
                                    width: '100%',
                                    height: 8,
                                    bgcolor: 'action.hover',
                                    borderRadius: '9999px',
                                    overflow: 'hidden',
                                    display: 'flex',
                                  }}
                                >
                                  {connectedPercent > 0 && (
                                    <Box
                                      sx={{
                                        height: '100%',
                                        bgcolor:
                                          health.status === 'healthy'
                                            ? JUGANU_GREEN
                                            : health.status === 'warning'
                                              ? JUGANU_YELLOW
                                              : JUGANU_RED,
                                        width: `${connectedPercent}%`,
                                        transition: 'width 0.3s ease',
                                      }}
                                    />
                                  )}
                                  {offlinePercent > 0 && (
                                    <Box
                                      sx={{
                                        height: '100%',
                                        bgcolor: 'action.disabled',
                                        width: `${offlinePercent}%`,
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ borderColor: 'divider' }}>
                              <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{total}</Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ borderColor: 'divider' }}>
                              <Chip
                                label={health.label}
                                size="small"
                                sx={{
                                  bgcolor: `${health.color}1A`,
                                  color: health.color,
                                  border: `1px solid ${health.color}33`,
                                  fontWeight: 800,
                                  fontSize: '0.62rem',
                                  height: 24,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.6,
                                  '& .MuiChip-label': { px: 1.5 },
                                }}
                                icon={
                                  <Box
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      bgcolor: health.color,
                                    }}
                                  />
                                }
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                      .filter(Boolean)}
                    {Object.keys(sourceData || {}).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary', borderColor: 'divider' }}>
                          No device type data available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box
                sx={{
                  px: 3,
                  py: 1.6,
                  bgcolor: 'action.hover',
                  borderTop: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                }}
              >
                <span>
                  Showing{' '}
                  {Object.keys(sourceData || {}).filter((key) => {
                    const data = sourceData[key];
                    const total =
                      typeof data === 'object' && data !== null
                        ? (data.connected || 0) + (data.offline || 0)
                        : typeof data === 'number'
                          ? data
                          : 0;
                    return total > 0;
                  }).length}{' '}
                  device types
                </span>
                <span style={{ opacity: 0.75 }}>Data refreshes automatically every 60s</span>
              </Box>
            </Paper>
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} xl={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Topology Metrics */}
              <Box>
                <Typography
                  sx={{
                    mb: 2,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: 1.6,
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <AccountTreeIcon sx={{ fontSize: 18 }} />
                  Topology Metrics
                </Typography>
                <Paper sx={{ borderRadius: 2, border: 1, borderColor: 'divider', p: 2, bgcolor: 'background.paper' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                          border: 1,
                          borderColor: 'divider',
                          height: 78,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 1.2 }}>
                          Max Nodes / CAP
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                          <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1.35rem' }}>
                            {maxNodesPerCap === null || maxNodesPerCap === undefined ? '-' : maxNodesPerCap}
                          </Typography>
                          <HubIcon sx={{ color: JUGANU_YELLOW, fontSize: 20, opacity: 0.55 }} />
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                          border: 1,
                          borderColor: 'divider',
                          height: 78,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 1.2 }}>
                          Min Nodes / CAP
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                          <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1.35rem' }}>
                            {minNodesPerCap === null || minNodesPerCap === undefined ? '-' : minNodesPerCap}
                          </Typography>
                          <HubIcon sx={{ color: JUGANU_YELLOW, fontSize: 20, opacity: 0.32 }} />
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          p: 1.6,
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                          border: 1,
                          borderColor: 'divider',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box>
                          <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: 1.2 }}>
                            Max Tree Depth
                          </Typography>
                          <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1.2rem', mt: 0.4 }}>
                            {maxTreeDepth === null || maxTreeDepth === undefined ? '-' : maxTreeDepth}{' '}
                            <Typography component="span" sx={{ fontSize: '0.75rem', fontFamily: 'inherit', fontWeight: 500, color: 'text.secondary' }}>
                              Hops
                            </Typography>
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end', height: 24 }}>
                          <Box sx={{ width: 6, height: 8, bgcolor: JUGANU_YELLOW, borderRadius: 0.5, opacity: 0.4 }} />
                          <Box sx={{ width: 6, height: 16, bgcolor: JUGANU_YELLOW, borderRadius: 0.5, opacity: 0.7 }} />
                          <Box sx={{ width: 6, height: 24, bgcolor: JUGANU_YELLOW, borderRadius: 0.5 }} />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>

              {/* Network Quality */}
              <Box>
                <Typography
                  sx={{
                    mb: 2,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: 1.6,
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <SpeedIcon sx={{ fontSize: 18 }} />
                  Network Quality
                </Typography>
                <Paper sx={{ borderRadius: 2, border: 1, borderColor: 'divider', p: 2.5, bgcolor: 'background.paper' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Avg Latency */}
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 0.75 }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>
                          Avg Latency
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {avgLatency === null || avgLatency === undefined ? (
                            <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1rem' }}>-</Typography>
                          ) : (
                            <>
                              <Chip
                                label="Good"
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.55rem',
                                  fontWeight: 900,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.6,
                                  bgcolor: `${getLatencyColor(avgLatency)}1A`,
                                  color: getLatencyColor(avgLatency),
                                  border: `1px solid ${getLatencyColor(avgLatency)}33`,
                                }}
                              />
                              <Typography sx={{ fontFamily: 'monospace', fontWeight: 900, lineHeight: 1, fontSize: '1.05rem' }}>
                                {Number(avgLatency).toFixed(0)}
                                <Typography component="span" sx={{ ml: 0.5, color: 'text.secondary', fontSize: '0.75rem', fontWeight: 600 }}>
                                  ms
                                </Typography>
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ height: 6, width: '100%', bgcolor: 'action.hover', borderRadius: '9999px', overflow: 'hidden' }}>
                        {avgLatency !== null && avgLatency !== undefined && (
                          <Box
                            sx={{
                              height: '100%',
                              bgcolor: getLatencyColor(avgLatency),
                              width: `${Math.min((avgLatency / 200) * 100, 100)}%`,
                              borderRadius: '9999px',
                              boxShadow: `0 0 10px ${getLatencyColor(avgLatency)}4D`,
                            }}
                          />
                        )}
                      </Box>
                    </Box>

                    {/* Packet Loss */}
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 0.75 }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>
                          Packet Loss
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {packetLoss === null || packetLoss === undefined ? (
                            <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1rem' }}>-</Typography>
                          ) : (
                            <>
                              <Chip
                                label="Good"
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.55rem',
                                  fontWeight: 900,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.6,
                                  bgcolor: `${getPacketLossColor(packetLoss)}1A`,
                                  color: getPacketLossColor(packetLoss),
                                  border: `1px solid ${getPacketLossColor(packetLoss)}33`,
                                }}
                              />
                              <Typography sx={{ fontFamily: 'monospace', fontWeight: 900, lineHeight: 1, fontSize: '1.05rem' }}>
                                {Number(packetLoss).toFixed(2)}
                                <Typography component="span" sx={{ ml: 0.5, color: 'text.secondary', fontSize: '0.75rem', fontWeight: 600 }}>
                                  %
                                </Typography>
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ height: 6, width: '100%', bgcolor: 'action.hover', borderRadius: '9999px', overflow: 'hidden' }}>
                        {packetLoss !== null && packetLoss !== undefined && (
                          <Box
                            sx={{
                              height: '100%',
                              bgcolor: getPacketLossColor(packetLoss),
                              width: `${Math.min(packetLoss * 10, 100)}%`,
                              borderRadius: '9999px',
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </Box>
          </Grid>

          {/* Software Versions Distribution */}
          <Grid item xs={12}>
            <Typography
              sx={{
                mb: 2,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: 1.6,
                fontSize: '0.75rem',
                color: 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CodeIcon sx={{ fontSize: 18 }} />
              Software Versions Distribution
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {Object.entries(softwareVersionsByType || {}).map(([deviceType, versions]) => {
                const versionCount =
                  typeof versions === 'object' && versions !== null ? Object.keys(versions).length : 0;
                const isExpanded = expandedVersions[deviceType];

                // Total devices per type (from sourceData)
                const deviceTypeData = sourceData?.[deviceType];
                let deviceTypeTotal = 0;
                if (deviceTypeData) {
                  if (typeof deviceTypeData === 'object' && deviceTypeData !== null && !Array.isArray(deviceTypeData)) {
                    deviceTypeTotal = (deviceTypeData.connected || 0) + (deviceTypeData.offline || 0);
                  } else if (typeof deviceTypeData === 'number') {
                    deviceTypeTotal = deviceTypeData;
                  }
                }

                const fragmentationDetected = versionCount > 2;

                return (
                  <Paper
                    key={deviceType}
                    sx={{
                      borderRadius: 2,
                      border: 1,
                      borderColor: isExpanded ? `${JUGANU_YELLOW}80` : 'divider',
                      overflow: 'hidden',
                      bgcolor: 'background.paper',
                      transition: 'all 0.2s',
                      '&:hover': { borderColor: JUGANU_YELLOW },
                      boxShadow: '0 10px 24px rgba(0,0,0,0.45)',
                    }}
                  >
                    <Box
                      onClick={() => toggleVersionExpand(deviceType)}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        '&:hover': { bgcolor: 'action.hover' },
                        transition: 'background-color 0.2s',
                        userSelect: 'none',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <ChevronRightIcon
                          sx={{
                            color: 'text.secondary',
                            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                          }}
                        />
                        <Typography sx={{ fontWeight: 900, fontSize: '0.95rem' }}>{deviceType}</Typography>
                        {fragmentationDetected ? (
                          <Chip
                            label="Fragmentation Detected"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.62rem',
                              fontWeight: 900,
                              textTransform: 'uppercase',
                              letterSpacing: 0.6,
                              bgcolor: `${JUGANU_RED}1A`,
                              color: JUGANU_RED,
                              border: `1px solid ${JUGANU_RED}33`,
                            }}
                          />
                        ) : (
                          <Chip
                            label={`${versionCount} Version${versionCount !== 1 ? 's' : ''}`}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.62rem',
                              fontWeight: 900,
                              textTransform: 'uppercase',
                              letterSpacing: 0.6,
                              bgcolor: 'action.hover',
                              color: 'text.secondary',
                              border: 1,
                              borderColor: 'divider',
                            }}
                          />
                        )}
                      </Box>
                      <Typography sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.8rem' }}>
                        {deviceTypeTotal} Devices
                      </Typography>
                    </Box>

                    <Collapse in={isExpanded}>
                      <Box sx={{ px: 3, pb: 2.5, pt: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
                        {isExpanded && typeof versions === 'object' && versions !== null && (
                          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                            {Object.entries(versions)
                              .sort(([, a], [, b]) => b - a)
                              .map(([version, count], index) => {
                                let versionLabel = 'Latest';
                                let labelColor = JUGANU_GREEN;
                                if (index > 0) {
                                  versionLabel = count < deviceTypeTotal * 0.1 ? 'Deprecated' : 'Legacy';
                                  labelColor = count < deviceTypeTotal * 0.1 ? JUGANU_RED : JUGANU_YELLOW;
                                }

                                return (
                                  <Grid item xs={12} sm={6} key={version}>
                                    <Paper
                                      sx={{
                                        p: 1.5,
                                        bgcolor: '#0A0F1A',
                                        border: 1,
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <Box>
                                        <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.9rem' }}>
                                          {version}
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: '0.62rem',
                                            fontWeight: 900,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.7,
                                            color: labelColor,
                                            mt: 0.2,
                                          }}
                                        >
                                          {versionLabel}
                                        </Typography>
                                      </Box>
                                      <Typography sx={{ fontFamily: 'monospace', fontWeight: 900 }}>
                                        {count}{' '}
                                        <Typography component="span" sx={{ color: 'text.secondary', fontFamily: 'inherit', fontWeight: 600, fontSize: '0.75rem' }}>
                                          Units
                                        </Typography>
                                      </Typography>
                                    </Paper>
                                  </Grid>
                                );
                              })}
                          </Grid>
                        )}
                      </Box>
                    </Collapse>
                  </Paper>
                );
              })}

              {Object.keys(softwareVersionsByType || {}).length === 0 && (
                <Paper sx={{ p: 3, textAlign: 'center', color: 'text.secondary', border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                  No software version data available
                </Paper>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </ThemeProvider>
  );
};

export default NetworkStatusPage;
